import { z } from "zod";
import { customerAudienceSchema } from "./audience.js";
import { journeyEventSchema, journeyStepSchema } from "./journey-event.js";
z.config({ jitless: true });
/**
 * The pilot cockpit's reads (2026-10-16): what needs a person, one book's whole journey, and
 * each customer. Ids, codes, counts and money only; an email or a name is never returned.
 */
export const adminObservabilitySchemaVersionSchema = z.literal("2026-10-16.1");
const id = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u);
const code = z.string().regex(/^[A-Z][A-Z0-9_]{0,95}$/u);
const count = z.number().int();
const micros = z.number().int().nonnegative();
const instant = z.string().datetime();
const cursor = z.string().min(1).max(2048);
/** Whose records: real customers only (not test, demo, staff or owner accounts), or everyone. */
export const adminObservabilityScopeSchema = z.enum(["REAL_CUSTOMERS", "EVERYONE"]);
const audience = z.union([customerAudienceSchema, z.literal("UNKNOWN")]);
export const adminJourneyBookSchema = z
    .object({
    bookId: id,
    customerId: id.optional(),
    audience: audience.optional(),
    testBook: z.boolean(),
    bookType: z
        .string()
        .regex(/^[a-z][a-z0-9-]{0,47}$/u)
        .optional(),
    step: journeyStepSchema.optional(),
    stepAt: instant.optional(),
    bookCreatedAt: instant.optional(),
    lastActivityAt: instant.optional(),
    lastEventType: code.optional(),
    lastJobTask: code.optional(),
    lastJobEvent: code.optional(),
    lastJobAt: instant.optional(),
    attentionCode: code.optional(),
    attentionTask: code.optional(),
    attentionAt: instant.optional(),
    activeJobs: count,
    jobsSucceeded: count,
    jobsNeedingAttention: count,
    jobsFailed: count,
    aiCostMicros: micros,
    picturesChecked: count,
    picturesFailedCheck: count,
    budgetSpentMicros: micros.optional(),
    budgetReservedMicros: micros.optional(),
    budgetLimitMicros: micros.optional(),
    budgetUpdatedAt: instant.optional(),
    firstFinishedAt: instant.optional(),
    firstPrintApprovedAt: instant.optional(),
    /** Its owner asked to delete it; it can be recovered until it is purged. */
    deletionRequestedAt: instant.optional(),
    purgedAt: instant.optional(),
    /** Its printed copy's latest state, and whether that went to a test printer. */
    printState: code.optional(),
    printStateAt: instant.optional(),
    printTest: z.boolean().optional(),
    firstPrintSentAt: instant.optional(),
})
    .strict();
export const adminJourneyCustomerSchema = z
    .object({
    customerId: id,
    audience: audience,
    accountCreatedAt: instant.optional(),
    firstSeenAt: instant.optional(),
    lastSeenAt: instant.optional(),
    lastEventType: code.optional(),
    booksStarted: count,
    booksFinished: count,
    booksPrintApproved: count,
    vouchersRedeemed: count,
    aiCostMicros: micros,
    emailProblems: count,
    supportRequests: count,
    /** Printed copies ordered, books whose copy reached a real printer, and errors seen. */
    printOrders: count,
    booksPrinted: count,
    errors: count,
    events: count,
    /** Shown to owners (owner decision, 2026-10-21); read from the profile, never the journey. */
    email: z.string().min(3).max(320).optional(),
})
    .strict();
export const adminJourneyEventSchema = journeyEventSchema
    .omit({ schemaVersion: true })
    .extend({ testBook: z.boolean().optional() });
export const adminEmailAttentionSchema = z
    .object({
    customerId: id.optional(),
    audience: audience,
    bookId: id.optional(),
    eventType: code,
    messageType: z
        .string()
        .regex(/^[a-z][a-z0-9_]{0,47}$/u)
        .optional(),
    attentionCode: code.optional(),
    attentionAt: instant,
})
    .strict();
export const adminSupportAttentionSchema = z
    .object({
    ticketId: id,
    customerId: id.optional(),
    audience: audience,
    bookId: id.optional(),
    ticketCategory: code.optional(),
    attentionAt: instant,
})
    .strict();
/**
 * A print order that needs a person (2026-10-17): printing flagged it, its check with the
 * printer is over half an hour overdue, so it has stopped being tracked, or it has sat in one
 * state longer than that state takes (a late printer). Sandbox orders
 * are tests, listed only for everyone; anything sent to the real printer is listed whoever
 * placed it, since it is printed and paid for.
 */
export const adminPrintOrderAttentionSchema = z
    .object({
    physicalOrderId: id,
    fulfillmentId: id.optional(),
    bookId: id.optional(),
    state: code,
    /**
     * When its record last changed: for NOT_CHECKED, when the printer was last asked; for LATE,
     * when it entered its state.
     */
    since: instant,
    reason: z.enum(["NEEDS_ATTENTION", "NOT_CHECKED", "LATE"]),
    /** For LATE: how long its state usually takes at most. */
    limitMinutes: z.number().int().positive().optional(),
    test: z.boolean(),
})
    .strict();
/** A customer's latest error: a page that failed, or requests the server failed (7 days). */
export const adminErrorAttentionSchema = z
    .object({
    customerId: id,
    audience: audience,
    bookId: id.optional(),
    eventType: z.enum(["CLIENT_ERROR", "API_ERROR"]),
    attentionCode: code.optional(),
    screen: z
        .string()
        .regex(/^\/[a-z0-9/:._-]{0,120}$/u)
        .optional(),
    routeKey: z.string().min(1).max(220).optional(),
    httpStatus: z.number().int().min(100).max(599).optional(),
    attentionAt: instant,
})
    .strict();
/** The pilot at a glance: totals over the customers in scope (pilot watch, 2026-10-17). */
export const adminObservabilitySummarySchema = z
    .object({
    customers: count,
    vouchersRedeemed: count,
    booksStarted: count,
    booksFinished: count,
    booksPrintApproved: count,
    printOrders: count,
    booksPrinted: count,
    errors: count,
    aiCostMicros: micros,
    /** More customers than were added up. */
    truncated: z.boolean(),
})
    .strict();
export const adminObservabilityAttentionCommandSchema = z
    .object({ scope: adminObservabilityScopeSchema.default("REAL_CUSTOMERS") })
    .strict();
export const adminObservabilityAttentionResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminObservabilitySchemaVersionSchema,
        scope: adminObservabilityScopeSchema,
        generatedAt: instant,
        /** Books whose latest AI job stopped and hasn't succeeded since. */
        stoppedBooks: z.array(adminJourneyBookSchema).max(100),
        /** Unfinished books nobody has worked on for a day. */
        idleBooks: z.array(adminJourneyBookSchema).max(100),
        /** Unfinished books holding reserved money with nothing running for an hour. */
        heldMoneyBooks: z.array(adminJourneyBookSchema).max(100),
        emailProblems: z.array(adminEmailAttentionSchema).max(100),
        supportWaiting: z.array(adminSupportAttentionSchema).max(100),
        summary: adminObservabilitySummarySchema,
        /** Customers whose page failed, or whose requests the server failed, this week. */
        errorCustomers: z.array(adminErrorAttentionSchema).max(100),
        /** Flagged first, then longest waiting first. */
        printOrders: z.array(adminPrintOrderAttentionSchema).max(100),
        /** Print orders couldn't be read just now; the other lists are complete. */
        printOrdersUnavailable: code.optional(),
        /** Lists that hold more than they show: the newest 100 are shown. */
        truncated: z
            .object({
            stoppedBooks: z.boolean(),
            idleBooks: z.boolean(),
            heldMoneyBooks: z.boolean(),
            emailProblems: z.boolean(),
            supportWaiting: z.boolean(),
            errorCustomers: z.boolean(),
            printOrders: z.boolean(),
        })
            .strict(),
    })
        .strict(),
})
    .strict();
export const adminBookJourneyCommandSchema = z
    .object({ bookId: id, cursor: cursor.optional() })
    .strict();
export const adminBookJourneyResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminObservabilitySchemaVersionSchema,
        book: adminJourneyBookSchema.nullable(),
        /** Oldest first. */
        events: z.array(adminJourneyEventSchema).max(200),
        nextCursor: cursor.optional(),
    })
        .strict(),
})
    .strict();
export const adminCustomerListCommandSchema = z
    .object({
    audience: customerAudienceSchema.default("CUSTOMER"),
    limit: z.number().int().positive().max(100).default(50),
    cursor: cursor.optional(),
})
    .strict();
export const adminCustomerListResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminObservabilitySchemaVersionSchema,
        audience: customerAudienceSchema,
        /** Most recently seen first. */
        customers: z.array(adminJourneyCustomerSchema).max(100),
        nextCursor: cursor.optional(),
    })
        .strict(),
})
    .strict();
export const adminCustomerDetailCommandSchema = z
    .object({
    customerId: id.optional(),
    /** Find a customer by the email they sign in with (2026-10-21). */
    email: z.string().trim().toLowerCase().email().max(254).optional(),
    cursor: cursor.optional(),
})
    .strict()
    .refine((command) => Boolean(command.customerId) !== Boolean(command.email), {
    message: "Look a customer up by their id or their email.",
});
/**
 * Owners find a customer by the start of their name (stage 2, 2026-11-02): the sign-in
 * identity's name, given name or family name. Owners only; the text searched for is never
 * recorded, only how many customers it found.
 */
export const adminCustomerSearchCommandSchema = z
    .object({
    namePrefix: z
        .string()
        .trim()
        .min(2)
        .max(64)
        .regex(/^[^\p{Cc}\p{Cf}]+$/u),
    limit: z.number().int().positive().max(20).default(10),
})
    .strict();
export const adminCustomerSearchMatchSchema = z
    .object({
    customerId: id,
    /** As the customer gave it at sign-up. */
    name: z.string().min(1).max(256),
    /** Their sign-in email, when it is verified. */
    email: z.string().min(3).max(320).optional(),
})
    .strict();
export const adminCustomerSearchResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminObservabilitySchemaVersionSchema,
        /** Ordered by name. */
        customers: z.array(adminCustomerSearchMatchSchema).max(20),
        /** More customers matched than are shown: type more of the name. */
        truncated: z.boolean(),
    })
        .strict(),
})
    .strict();
export const adminCustomerDetailResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminObservabilitySchemaVersionSchema,
        customer: adminJourneyCustomerSchema.nullable(),
        books: z.array(adminJourneyBookSchema).max(100),
        /** Newest first. */
        events: z.array(adminJourneyEventSchema).max(200),
        nextCursor: cursor.optional(),
    })
        .strict(),
})
    .strict();
/**
 * AI operations and cost (2026-10-18): AI work added up by day, step and model, from the
 * journey's own rollups. Totals only: never a customer or a book.
 */
export const adminAiOperationsCommandSchema = z
    .object({
    scope: adminObservabilityScopeSchema.default("REAL_CUSTOMERS"),
    days: z.union([z.literal(7), z.literal(30)]).default(7),
})
    .strict();
const aiCounts = {
    jobs: count,
    succeeded: count,
    stopped: count,
    failed: count,
    costMicros: micros,
    /** Run time added up over the steps that report one, and how many did. */
    durationMs: count,
    timed: count,
};
/** Steps by run time: under 10 s, 30 s, 1 min, 3 min, 10 min, and longer. */
const aiDurations = z.array(count).length(6);
export const adminAiOperationsResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminObservabilitySchemaVersionSchema,
        scope: adminObservabilityScopeSchema,
        days: z.union([z.literal(7), z.literal(30)]),
        generatedAt: instant,
        totals: z.object({ ...aiCounts, booksFinished: count }).strict(),
        /** Oldest first, every day of the range. */
        byDay: z
            .array(z
            .object({
            day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/u),
            jobs: count,
            stopped: count,
            failed: count,
            costMicros: micros,
            booksFinished: count,
        })
            .strict())
            .max(31),
        /** Most spent first. */
        byTask: z
            .array(z.object({ task: code, ...aiCounts, durations: aiDurations }).strict())
            .max(100),
        byModel: z
            .array(z
            .object({
            provider: code,
            modelId: z.string().regex(/^[a-z0-9][a-z0-9._:-]{0,95}$/iu),
            ...aiCounts,
        })
            .strict())
            .max(100),
    })
        .strict(),
})
    .strict();
/**
 * Reliability (2026-10-18): what is firing, what failed and is waiting, how the customer API and
 * the functions behind it are doing, and which release is live. Service names and counts only.
 */
export const adminReliabilityCommandSchema = z.object({}).strict();
const resourceName = z.string().min(1).max(255);
export const adminReliabilityResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminObservabilitySchemaVersionSchema,
        generatedAt: instant,
        release: z
            .object({
            sha: z
                .string()
                .regex(/^[0-9a-f]{40}$/u)
                .optional(),
            updatedAt: instant.optional(),
        })
            .strict(),
        /** Firing first, then needing data, then the rest; at most 500. */
        alarms: z
            .array(z
            .object({
            name: resourceName,
            label: z.string().min(1).max(160),
            state: z.enum(["ALARM", "INSUFFICIENT_DATA", "OK"]),
            since: instant.optional(),
        })
            .strict())
            .max(500),
        /** Failed-message queues with messages waiting, most first. */
        queues: z
            .array(z
            .object({ name: resourceName, label: z.string().min(1).max(160), waiting: count })
            .strict())
            .max(200),
        /** The customer API over the last 24 hours. */
        api: z
            .object({ requests: count, clientErrors: count, serverErrors: count })
            .strict()
            .optional(),
        /** Functions that failed over the last 24 hours, most first. */
        functionErrors: z
            .array(z
            .object({ name: resourceName, label: z.string().min(1).max(160), errors: count })
            .strict())
            .max(50),
        /** Parts that couldn't be read just now; the rest is complete. */
        unavailable: z.array(z.enum(["ALARMS", "QUEUES", "API", "FUNCTIONS", "RELEASE"])),
    })
        .strict(),
})
    .strict();
//# sourceMappingURL=admin-operations-observability.js.map